import apiInstance from "./index";
import abacCache from "utils/abacCache";

/**
 * ABAC API Service
 * Handles all authorization-related API calls
 */

// Page guards (AbacProtectedRoute) block the whole page on the context and
// evaluate calls below. Without a timeout a request the server never answers
// leaves the user on a bare spinner indefinitely; with one it fails, and the
// guard offers a Retry instead.
export const ABAC_REQUEST_TIMEOUT_MS = 15000;

/**
 * Get current user's authorization context
 * @returns {Promise} User context including roles, attributes, and permissions
 */
export const getUserContext = async () => {
  try {
    const response = await apiInstance.get("/authorization/context", {
      timeout: ABAC_REQUEST_TIMEOUT_MS,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Evaluate a single permission
 * @param {Object} dto - Permission evaluation request
 * @param {string} dto.resourceType - Type of resource
 * @param {string} dto.action - Action to perform
 * @param {string} [dto.resourceId] - Optional specific resource ID
 * @param {Object} [dto.context] - Optional additional context
 * @returns {Promise} Permission evaluation result
 */
export const evaluatePermission = async (dto) => {
  try {
    // Check cache first
    const cacheKey = abacCache.constructor.generateKey(
      dto.resourceType,
      dto.action,
      dto.resourceId
    );

    const cachedResult = abacCache.get(cacheKey);
    if (cachedResult !== null) {
      return {
        ...cachedResult,
        fromCache: true,
      };
    }

    // Make API call
    const response = await apiInstance.post("/authorization/evaluate", dto, {
      timeout: ABAC_REQUEST_TIMEOUT_MS,
    });
    const result = response.data;

    // Cache the result - API returns { success, data: { granted } }
    if (result && result.data?.granted !== undefined) {
      abacCache.set(cacheKey, result.data);
    }

    return {
      ...result,
      fromCache: false,
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Evaluate multiple permissions in a single batch request
 * @param {Array} permissions - Array of permission objects
 * @param {string} permissions[].resourceType - Type of resource
 * @param {string} permissions[].action - Action to perform
 * @param {string} [permissions[].resourceId] - Optional specific resource ID
 * @returns {Promise} Array of permission evaluation results
 */
export const evaluateBatch = async (permissions) => {
  try {
    if (!Array.isArray(permissions) || permissions.length === 0) {
      throw new Error("Permissions must be a non-empty array");
    }

    const response = await apiInstance.post("/authorization/evaluate-batch", {
      permissions,
    });

    // API returns { success, data: { results: [...] } }
    const results = response.data?.data?.results || response.data?.results || [];

    // Cache each result
    if (Array.isArray(results)) {
      results.forEach((result, index) => {
        if (result && result.granted !== undefined) {
          const permission = permissions[index];
          const cacheKey = abacCache.constructor.generateKey(
            permission.resourceType,
            permission.action,
            permission.resourceId
          );
          abacCache.set(cacheKey, result);
        }
      });
    }

    return results;
  } catch (error) {
    throw error;
  }
};

/**
 * Get all permitted actions for a specific resource type
 * @param {string} resourceType - Type of resource
 * @returns {Promise} List of permitted actions
 */
export const getPermittedActions = async (resourceType) => {
  try {
    const response = await apiInstance.get(
      `/authorization/permitted-actions/${resourceType}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get cached permission result
 * @param {string} resourceType - Type of resource
 * @param {string} action - Action to perform
 * @param {string} [resourceId] - Optional specific resource ID
 * @returns {Object|null} Cached result or null if not found
 */
export const getCachedPermission = (resourceType, action, resourceId = null) => {
  const cacheKey = abacCache.constructor.generateKey(
    resourceType,
    action,
    resourceId
  );
  return abacCache.get(cacheKey);
};

/**
 * Invalidate cache entries by pattern
 * @param {string} pattern - Pattern to match (e.g., "device:*")
 * @returns {number} Number of entries invalidated
 */
export const invalidateCache = (pattern = "*") => {
  return abacCache.invalidate(pattern);
};

/**
 * Clear all cached permissions
 */
export const clearCache = () => {
  abacCache.clear();
};

/**
 * Get cache statistics
 * @returns {Object} Cache statistics
 */
export const getCacheStats = () => {
  return abacCache.stats();
};

/**
 * Get all capabilities for the current user
 * Returns all permitted actions grouped by resource type, plus feature flags
 * This is the most efficient way to gate UI elements
 * @param {string} [resourceTypes] - Optional comma-separated list of resource types to filter
 * @returns {Promise} User capabilities including permitted actions and feature flags
 */
export const getCapabilities = async (resourceTypes = null, signal) => {
  try {
    const params = resourceTypes ? { resourceTypes } : {};
    const response = await apiInstance.get("/authorization/me/capabilities", { params, signal });
    return response.data;
  } catch (error) {
    throw error;
  }
};
